import { Offer, StatusOffer } from '@agroone/entities'

/**
 * Business rules of the offer priorisation (assigning offers to a sector and a confirmed
 * harvest date, then ordering them by priority within each sector/date cell).
 */

/** Colors encoding how close the harvest date of an offer is, used across priorisation views. */
export const PRIORITY_COLORS = {
  /** More than one day late. */
  late: '#ef7159',
  /** Yesterday. */
  dayMinus1: '#FBC503',
  /** Today. */
  day: '#C5CF00',
  /** Tomorrow. */
  dayPlus1: '#67C6E8',
  /** The day after tomorrow. */
  dayPlus2: '#bb7ee8',
  /** Later than the day after tomorrow. */
  later: '#CCCCCC',
  /** Offer closed, whatever its date. */
  closed: '#636363',
} as const

/** Statuses of offers that can still be dragged around on the priorisation screen. */
export const PRIORISATION_DRAGGABLE_STATUSES: readonly StatusOffer[] = [
  StatusOffer.offerCreated,
  StatusOffer.offerPriorised,
  StatusOffer.offerPlanified,
]

const DAY_MS = 24 * 60 * 60 * 1000

const startOfDay = (date: Date): number => {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy.getTime()
}

export const PriorisationRules = {
  /**
   * Color for a date relative to today: yesterday, today, tomorrow and the day after each
   * have their own color; anything more than one day late is "late", anything later than
   * D+2 is "later".
   */
  getColorForDate: (date: Date, today: Date = new Date()): string => {
    const diffInDays = Math.floor((startOfDay(date) - startOfDay(today)) / DAY_MS)
    switch (diffInDays) {
      case -1:
        return PRIORITY_COLORS.dayMinus1
      case 0:
        return PRIORITY_COLORS.day
      case 1:
        return PRIORITY_COLORS.dayPlus1
      case 2:
        return PRIORITY_COLORS.dayPlus2
      default:
        return diffInDays > 2 ? PRIORITY_COLORS.later : PRIORITY_COLORS.late
    }
  },

  /**
   * Color for an offer: a closed offer is always grey, otherwise the color of its forecast
   * harvest date.
   */
  getColorForOffer: (offer: Pick<Offer, 'status' | 'forecastHarvestDate'>, today: Date = new Date()): string => {
    if (offer.status === StatusOffer.offerClose) {
      return PRIORITY_COLORS.closed
    }
    return PriorisationRules.getColorForDate(new Date(offer.forecastHarvestDate ?? 0), today)
  },

  /** Whether an offer can still be dragged on the priorisation screen. */
  isDraggable: (offer: Pick<Offer, 'status'>): boolean => PRIORISATION_DRAGGABLE_STATUSES.includes(offer.status),

  /** An offer can be removed from the priorisation list only while it is merely priorised. */
  canBeRemovedFromPriorisation: (offer: Pick<Offer, 'status'>): boolean => offer.status === StatusOffer.offerPriorised,

  /**
   * Comparator ordering offers of a sector/date cell: by ascending priority, offers without
   * a priority last.
   */
  compareByPriority: (a: Pick<Offer, 'priority'>, b: Pick<Offer, 'priority'>): number => {
    if (a.priority == null && b.priority == null) {
      return 0
    }
    if (a.priority == null) {
      return 1
    }
    if (b.priority == null) {
      return -1
    }
    return a.priority - b.priority
  },

  /**
   * Priorities to persist after a reorder: offers are renumbered 1..n in their new order.
   * Returns the id/priority pairs without mutating the input.
   */
  renumberPriorities: (offers: Pick<Offer, 'id'>[]): { id: number | null; priority: number }[] =>
    (offers ?? []).map((offer, index) => ({ id: offer.id, priority: index + 1 })),

  /**
   * Prioritising an offer means giving it a sector and a confirmed harvest date; removing it
   * from the priorisation clears both.
   */
  getPriorisationPatch: (groupCode: string | null, confirmedHarvestDate: string | null) => ({
    groupCode,
    confirmedHarvestDate,
  }),
} as const
